import {
    Organization,
    OrganizationServicesOffered,
} from "@common/types/organization";
import { FirmSize } from "@common/types/entity";
import { FC } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useUpdateOrganizationProfileMutation } from "@api/organization.api";
import { Picker } from "@design-system/Picker/Picker.lib";
import {
    firmSizeLabels,
    servicesOfferedLabels,
} from "@components/entity/constants";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { MultiSelectRow } from "./MultiSelectRow";
import { buildProfilePayload } from "./buildProfilePayload";

const firmSizeItems = Object.values(FirmSize).map((v) => ({
    value: v,
    label: firmSizeLabels[v],
}));

const servicesOfferedItems = Object.values(OrganizationServicesOffered).map(
    (v) => ({ value: v, label: servicesOfferedLabels[v] }),
);

const schema = z.object({
    firmSize: z.nativeEnum(FirmSize, {
        errorMap: () => ({ message: "Please select a firm size" }),
    }),
    servicesOffered: z.array(z.nativeEnum(OrganizationServicesOffered)),
});

interface FormValues {
    firmSize: FirmSize | undefined;
    servicesOffered: OrganizationServicesOffered[];
}

interface Props {
    organization: Organization;
    onClose(): void;
}

export const EditFirmSizeModal: FC<Props> = ({ organization, onClose }) => {
    const { toast, error } = useToaster();
    const updateProfile = useUpdateOrganizationProfileMutation();
    const profile = organization.profile;

    const formik = useFormik<FormValues>({
        initialValues: {
            firmSize: profile?.firmSize,
            servicesOffered: profile?.servicesOffered ?? [],
        },
        validate: validateFormikWithZodSchema(schema),
        onSubmit: submitHelper({
            handler: async ({ firmSize, servicesOffered }) => {
                await updateProfile.mutateAsync({
                    organizationId: organization.id,
                    payload: buildProfilePayload(profile, {
                        firmSize,
                        servicesOffered,
                    }),
                });
                toast("Firm basics updated");
                onClose();
            },
            failureHandler: async (err) => {
                // eslint-disable-next-line no-console
                console.error("Failed to save firm basics", err);
                error("Failed to save firm basics");
            },
        }),
    });

    const { values, setFieldValue, setFieldTouched } = formik;

    return (
        <FormikProvider value={formik}>
            <VStack as={Form} gap={16}>
                <FormikField<FirmSize | undefined>
                    name="firmSize"
                    label="Number of clients served"
                    control="button"
                >
                    {({ helpers, field, invalid }) => (
                        <Picker.Select
                            items={firmSizeItems}
                            value={field.value ?? null}
                            onValueChange={(value) => {
                                void helpers.setValue(value ?? undefined);
                            }}
                            placeholder="Select"
                            aria-invalid={invalid}
                            trigger={
                                <Picker.Button
                                    variant="primary"
                                    size="xl"
                                    block
                                />
                            }
                        />
                    )}
                </FormikField>

                <MultiSelectRow
                    name="servicesOffered"
                    label="Services offered"
                    items={servicesOfferedItems}
                    value={values.servicesOffered}
                    onChange={(v) => {
                        void setFieldValue("servicesOffered", v);
                    }}
                    onBlur={() => {
                        void setFieldTouched("servicesOffered", true);
                    }}
                />

                <HStack as="footer" justify="end">
                    <ButtonWithLoader
                        size="xl"
                        loading={formik.isSubmitting}
                        type="submit"
                        variant="primary"
                    >
                        Save
                    </ButtonWithLoader>
                </HStack>
            </VStack>
        </FormikProvider>
    );
};
